import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';

import Modal from 'modules/views/shared/Modal';
import Select from 'modules/views/shared/Select';
import TextField from 'modules/views/shared/TextField';
import DatePicker from 'modules/views/shared/DatePicker';
import FileUpload, { IUploadedFile } from 'modules/views/shared/FileUpload';
import { useAppDispatch } from 'modules/state-mgmt/store';
import {
  createWorkerThirdPartyDrugTestResult,
  updateWorkerThirdPartyDrugTestResult,
} from 'modules/state-mgmt/workerData';
import { prettifyErrorResponse } from 'utils';
import { IThirdPartyResultSaveOutcome } from 'models/drugTesting';

import { IThirdPartyTestResult } from '../types';
import { useStylesDrugTestingModal } from '../styles';
import { useUploadResultsOptions } from '../hooks/useUploadResultsOptions';

export interface IUploadResultsForm {
  projectId: string;
  testDate: moment.Moment | null;
  facilityName: string;
  panelId: string;
  typeId: string;
  reason: string;
  resultId: string;
  files: IUploadedFile[];
}

interface IUploadResultsModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // When provided, the modal opens in edit mode prefilled from this result.
  testResult?: IThirdPartyTestResult | null;
}

const MAX_FILES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const INITIAL_FORM: IUploadResultsForm = {
  projectId: '',
  testDate: null,
  facilityName: '',
  panelId: '',
  typeId: '',
  reason: '',
  resultId: '',
  files: [],
};

// Ensures the currently-selected value is always present as an option (and thus displayed),
// even if it's been filtered out of / not yet loaded into the fetched list (used in edit mode).
const withSelectedOption = (
  options: Array<{ value: string; label: string; [key: string]: any }>,
  value?: string,
  label?: string,
) => {
  if (!value || options.some(option => option.value === value)) return options;
  return [{ value, label: label || '' }, ...options];
};

const UploadResultsModal: React.FC<IUploadResultsModalProps> = ({ isVisible, onClose, onConfirm, testResult }) => {
  const classes = useStylesDrugTestingModal();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isEditMode = !!testResult;

  const [formData, setFormData] = useState<IUploadResultsForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof IUploadResultsForm, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);
  // In create mode the record is created before files upload. If the upload fails, the record
  // already exists — we keep its id here so a retry edits that record instead of creating a duplicate.
  const [createdResultId, setCreatedResultId] = useState<string | null>(null);

  const { projects, panels, types, results, isLoadingProjects, isLoadingPanels, isLoadingTypes } =
    useUploadResultsOptions(formData.projectId, formData.panelId, isVisible);

  // In edit mode, guarantee the result's current selections are always shown.
  const projectOptions = useMemo(
    () => withSelectedOption(projects, testResult?.projectId, testResult?.projectName),
    [projects, testResult],
  );
  const panelOptions = useMemo(
    () => withSelectedOption(panels, testResult?.panelId, testResult?.panelName),
    [panels, testResult],
  );
  const typeOptions = useMemo(
    () => withSelectedOption(types, testResult?.drugTestingTypeId, testResult?.typeName),
    [types, testResult],
  );
  const resultOptions = useMemo(
    () =>
      withSelectedOption(
        results,
        testResult?.resultValue != null ? String(testResult.resultValue) : undefined,
        testResult?.testResult,
      ),
    [results, testResult],
  );

  useEffect(() => {
    if (!isVisible) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setIsSubmitting(false);
      setDeletedFileIds([]);
      setCreatedResultId(null);
      return;
    }
    setCreatedResultId(null);
    if (testResult) {
      setFormData({
        projectId: testResult.projectId || '',
        testDate: testResult.testedAt ? moment(testResult.testedAt) : null,
        facilityName: testResult.facilityName && testResult.facilityName !== '--' ? testResult.facilityName : '',
        panelId: testResult.panelId || '',
        typeId: testResult.drugTestingTypeId || '',
        reason: testResult.reason && testResult.reason !== '--' ? testResult.reason : '',
        resultId: testResult.resultValue != null ? String(testResult.resultValue) : '',
        // Existing documents carry a server id + url (no File) so the thunk keeps them by fileId.
        files: (testResult.attachments || []).map(a => ({ id: a.id, name: a.fileName, url: a.url })),
      });
      setDeletedFileIds([]);
    }
  }, [isVisible, testResult]);

  // Clean up blob URLs on component unmount
  useEffect(() => {
    return () => {
      formData.files
        .filter(f => f.url?.startsWith('blob:'))
        .forEach(f => URL.revokeObjectURL(f.url!));
    };
  }, []);

  const handleFieldChange = <K extends keyof IUploadResultsForm>(field: K, value: IUploadResultsForm[K]) => {
    if (field === 'files') {
      const newFiles = value as IUploadedFile[];
      const oldFiles = formData.files;

      // Track which files were removed
      const removedFiles = oldFiles.filter(
        oldFile => !newFiles.some(newFile => newFile.id === oldFile.id),
      );

      // For each removed file: if it's an existing file (has http URL), track for deletion.
      // If it's a new file (has blob URL), clean up the blob URL.
      removedFiles.forEach(removedFile => {
        if (removedFile.url?.startsWith('http')) {
          setDeletedFileIds(prev =>
            prev.includes(removedFile.id) ? prev : [...prev, removedFile.id]
          );
        } else if (removedFile.url?.startsWith('blob:')) {
          URL.revokeObjectURL(removedFile.url);
        }
      });
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Project drives the panel/type lists — clear all dependents when it changes.
  const handleProjectChange = (projectId: string) => {
    setFormData(prev => ({ ...prev, projectId, typeId: '', panelId: '', resultId: '' }));
    setErrors(prev => ({ ...prev, projectId: undefined }));
  };

  // Panel drives the available Result options — clear the result when it changes.
  const handlePanelChange = (panelId: string) => {
    setFormData(prev => ({ ...prev, panelId, resultId: '' }));
    setErrors(prev => ({ ...prev, panelId: undefined }));
  };

  const validateForm = () => {
    const nextErrors: Partial<Record<keyof IUploadResultsForm, string>> = {};

    if (!formData.projectId) nextErrors.projectId = t('myDrugTesting.uploadResults.errors.projectRequired');
    if (!formData.testDate) nextErrors.testDate = t('myDrugTesting.uploadResults.errors.testDateRequired');
    if (!formData.facilityName.trim())
      nextErrors.facilityName = t('myDrugTesting.uploadResults.errors.facilityNameRequired');
    if (!formData.panelId) nextErrors.panelId = t('myDrugTesting.uploadResults.errors.panelRequired');
    if (!formData.typeId) nextErrors.typeId = t('myDrugTesting.uploadResults.errors.typeRequired');
    if (!formData.reason.trim()) nextErrors.reason = t('myDrugTesting.uploadResults.errors.reasonRequired');
    if (!formData.resultId) nextErrors.resultId = t('myDrugTesting.uploadResults.errors.resultRequired');
    if (formData.files.length === 0) nextErrors.files = t('myDrugTesting.uploadResults.errors.filesRequired');

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // After a partial upload, convert the files that DID upload from "new" (carrying a File) to
  // "existing" (carrying the server fileId). On the next Save they're sent with their fileId, so the
  // set-state PATCH keeps them and only the still-failed files get re-uploaded.
  // uploadedFiles[].index is the position among the new files (those with a File), in order.
  const markUploadedFilesAsSaved = (uploadedFiles: IThirdPartyResultSaveOutcome['uploadedFiles']) => {
    if (uploadedFiles.length === 0) return;
    const serverFileIdByNewIndex = new Map(uploadedFiles.map(u => [u.index, u.fileId]));
    setFormData(prev => {
      let newFileIndex = -1;
      const files = prev.files.map(file => {
        if (!file.file) return file; // already an existing file — leave untouched
        newFileIndex += 1;
        const serverFileId = serverFileIdByNewIndex.get(newFileIndex);
        if (!serverFileId) return file; // this new file failed — keep its File so it can be retried
        // Uploaded: free the local blob and keep it by server id so it isn't re-uploaded.
        if (file.url?.startsWith('blob:')) URL.revokeObjectURL(file.url);
        return { id: serverFileId, name: file.name } as IUploadedFile;
      });
      return { ...prev, files };
    });
  };

  const handleConfirm = () => {
    if (!validateForm()) return;

    const input = {
      projectId: formData.projectId,
      panelId: formData.panelId,
      drugTestingTypeId: formData.typeId,
      testedAt: formData.testDate!.clone().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      clinicName: formData.facilityName.trim(),
      reason: formData.reason.trim(),
      result: parseInt(formData.resultId, 10),
      // New files carry a File (fileId null); existing files keep their server id so they're preserved.
      files: formData.files.map(f => ({ fileId: f.file ? null : f.id, name: f.name, file: f.file })),
      deletedFileIds: deletedFileIds.length > 0 ? deletedFileIds : undefined,
    };

    // Edit an existing record (edit mode) or one created during a previous failed attempt (retry).
    // Either way we update that record so a retry never creates a duplicate.
    const existingResultId = testResult?.id || createdResultId;
    const request = existingResultId
      ? dispatch(updateWorkerThirdPartyDrugTestResult({ resultId: existingResultId, input }))
      : dispatch(createWorkerThirdPartyDrugTestResult(input));

    setIsSubmitting(true);
    request
      .unwrap()
      .then((outcome: IThirdPartyResultSaveOutcome) => {
        const savedMessage = isEditMode
          ? t('myDrugTesting.uploadResults.updateSuccessMessage')
          : t('myDrugTesting.uploadResults.successMessage');
        enqueueSnackbar(savedMessage, { variant: 'success' });

        if (outcome.failedCount > 0) {
          // The record saved, but some files failed to upload. Keep the modal open so the user can
          // retry. Mark the files that DID upload as saved so the retry won't re-upload them, and
          // remember the record id so a retry edits this record instead of creating a duplicate.
          markUploadedFilesAsSaved(outcome.uploadedFiles);
          if (!testResult) setCreatedResultId(outcome.resultId);
          enqueueSnackbar(
            t('myDrugTesting.uploadResults.errors.filesUploadFailed', { count: outcome.failedCount }),
            { variant: 'error' },
          );
          return;
        }

        onConfirm();
      })
      .catch((error: { message?: string; resultId?: string } | unknown) => {
        // The record may have been created before a PATCH/save error. Remember its id so the next
        // Save retries against it (as an edit) instead of creating a duplicate. Keep the modal open.
        const resultId = (error as { resultId?: string })?.resultId;
        if (resultId && !testResult) setCreatedResultId(resultId);
        enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const content = (
    <div className={classes.content}>
      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.uploadResults.selectProject')}
          required={true}
          disabled={isEditMode || isLoadingProjects}
          loading={isLoadingProjects}
          options={projectOptions}
          selectedValue={formData.projectId}
          valueAsSelectedValue={true}
          error={errors.projectId}
          testId="upload-results-project-select"
          onChange={value => handleProjectChange(value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <DatePicker
          inputLabel={t('myDrugTesting.uploadResults.enterTestDate')}
          required={true}
          value={formData.testDate}
          error={errors.testDate}
          maxDate={moment()}
          testId="upload-results-test-date-picker"
          onChange={value => handleFieldChange('testDate', value || null)}
        />
      </div>

      <div className={classes.fieldContainer}>
        <TextField
          label={t('myDrugTesting.uploadResults.facilityName')}
          placeholder={t('myDrugTesting.uploadResults.facilityName')}
          value={formData.facilityName}
          error={errors.facilityName}
          testId="upload-results-facility-name-input"
          onChange={e => handleFieldChange('facilityName', e.target.value)}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.uploadResults.panel')}
          required={true}
          disabled={isEditMode || !formData.projectId || isLoadingPanels}
          loading={isLoadingPanels}
          options={panelOptions}
          selectedValue={formData.panelId}
          valueAsSelectedValue={true}
          error={errors.panelId}
          testId="upload-results-panel-select"
          onChange={value => handlePanelChange(value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.uploadResults.type')}
          required={true}
          disabled={!formData.projectId || isLoadingTypes}
          loading={isLoadingTypes}
          options={typeOptions}
          selectedValue={formData.typeId}
          valueAsSelectedValue={true}
          error={errors.typeId}
          testId="upload-results-type-select"
          onChange={value => handleFieldChange('typeId', value || '')}
        />
      </div>

      <div className={classes.fieldContainer}>
        <TextField
          label={t('myDrugTesting.uploadResults.reason')}
          placeholder={t('myDrugTesting.uploadResults.reason')}
          value={formData.reason}
          error={errors.reason}
          testId="upload-results-reason-input"
          onChange={e => handleFieldChange('reason', e.target.value)}
        />
      </div>

      <div className={classes.fieldContainer}>
        <Select
          inputLabel={t('myDrugTesting.uploadResults.result')}
          required={true}
          disabled={!formData.panelId}
          options={resultOptions}
          selectedValue={formData.resultId}
          valueAsSelectedValue={true}
          error={errors.resultId}
          testId="upload-results-result-select"
          onChange={value => handleFieldChange('resultId', value || '')}
        />
        {!formData.panelId && (
          <p className={classes.fieldHint} data-testid="upload-results-panel-hint">
            {t('myDrugTesting.uploadResults.selectPanelFirst')}
          </p>
        )}
      </div>

      <FileUpload
        files={formData.files}
        onChange={files => handleFieldChange('files', files)}
        maxFiles={MAX_FILES}
        maxFileSize={MAX_FILE_SIZE}
        accept=".pdf,.png,.jpg,.jpeg"
        uploadHintPrefix={t('myDrugTesting.uploadResults.upload.hintPrefix')}
        uploadHint={t('myDrugTesting.uploadResults.upload.hint')}
        maxFilesHint={t('myDrugTesting.uploadResults.upload.maxFiles', { count: MAX_FILES })}
        error={errors.files}
        maxFileSizeError={t('myDrugTesting.uploadResults.errors.fileTooLarge', { size: 5 })}
        invalidFileTypeError={t('myDrugTesting.uploadResults.errors.invalidFileType')}
        onError={message => setErrors(prev => ({ ...prev, files: message }))}
        disabled={isSubmitting}
        testId="upload-results-files"
      />
    </div>
  );

  return (
    <Modal
      title={isEditMode ? t('myDrugTesting.uploadResults.editTitle') : t('myDrugTesting.uploadResults.title')}
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      disableScrollLock={true}
      disableBackdropClose={isSubmitting}
      hideCloseIcon={isSubmitting}
      testId="upload-results-modal"
      Content={content}
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.cancelButton,
          testId: 'upload-results-cancel-button',
          disabled: isSubmitting,
          onClick: handleClose,
        },
        {
          label: t('myDrugTesting.uploadResults.confirm'),
          styleClass: classes.confirmButton,
          testId: 'upload-results-confirm-button',
          isLoading: isSubmitting,
          onClick: handleConfirm,
        },
      ]}
      onClose={handleClose}
    />
  );
};

export default memo(UploadResultsModal);
